import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Button,
  FormControlLabel,
  FormHelperText,
  Grid,
  Switch,
} from "@mui/material";

import AppForm from "components/Shared/AppForm";
import BaseTextField from "components/Shared/Base/BaseTextField";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import RichTextEditor from "components/Shared/RichTextEditor";
import BaseSelect from "components/Shared/Base/BaseSelect";

import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import { useAuth } from "hooks";

import { formHookInputHelper } from "utils/formHookField";
import { useLazyGetDDLCustomerQuery } from "store/services/customerApi";
import { useLazyGetDDLContractorListQuery } from "store/services/contractorApi";

const INITIAL_VALUE = {
  account: "",
  entity: "",
  accountName: "",
  accountNumber: "",
  description: "",
  sourceModel: "",
  sourceModelRef: "",
  isActive: true,
};

const PAYMENT_ACCOUNT_SOURCE_MODEL = {
  VENDOR_PROFILE: "Vendor Profile",
  CUSTOMER_PROFILE: "Customer Profile",
  CONTRACTOR_PROFILE: "Contractor Profile",
};
const PaymentAccountForm = ({ paymentAccountDetail, isBusy, onSubmit }) => {
  const navigate = useNavigate();

  const {
    viewEntity,
    isAccountAdmin,
    isSystemAdmin,

    isEntityAdmin,
  } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_VALUE,
      account: viewEntity?.entity?.account,
      entity: viewEntity?.entity,
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchSourceModel = useWatch({ control, name: "sourceModel" });

  // RTK QUERY

  const {
    data: accounts = [],
    isLoading: loadingAccounts,
    isFetching: fetchingAccounts,
  } = useGetDDLAccountListQuery();

  const [
    fetchEntities,
    {
      data: entities = [],
      isLoading: loadingEntities,
      isFetching: fetchingEntities,
    },
  ] = useLazyGetDDLEntityListQuery();

  const [
    fetchVendors,
    { data: vendorRes, isLoading: loadingVendors, isFetching: fetchingVendors },
  ] = useLazyGetDdlVendorProfileQuery();

  const [
    fetchCustomers,
    {
      data: customerRes,
      isLoading: loadingCustomer,
      isFetching: fetchingCustomer,
    },
  ] = useLazyGetDDLCustomerQuery();
  const [
    fetchContractors,
    {
      data: contractorRes,
      isLoading: loadingContractor,
      isFetching: fetchingContractor,
    },
  ] = useLazyGetDDLContractorListQuery();

  const sourceRefLabel =
    _watchSourceModel === PAYMENT_ACCOUNT_SOURCE_MODEL.CUSTOMER_PROFILE
      ? "Customer Profile"
      : _watchSourceModel === PAYMENT_ACCOUNT_SOURCE_MODEL.CONTRACTOR_PROFILE
      ? "Contractor Profile"
      : "Vendor Profile";

  const originSourceRefInput = React.useMemo(() => {
    switch (_watchSourceModel) {
      case PAYMENT_ACCOUNT_SOURCE_MODEL.VENDOR_PROFILE:
        return {
          data: vendorRes,
          getOptionLabel: (opt) =>
            opt ? `${opt?.vendor?.name} - ${opt?.shopName}` : "",
        };

      case PAYMENT_ACCOUNT_SOURCE_MODEL.CUSTOMER_PROFILE:
        return {
          data: customerRes,
          getOptionLabel: (opt) =>
            opt ? `${opt?.customerUser?.name} - ${opt?.phone}` : "",
        };

      case PAYMENT_ACCOUNT_SOURCE_MODEL.CONTRACTOR_PROFILE:
        return {
          data: contractorRes,
          getOptionLabel: (opt) => (opt ? `${opt?.name} - ${opt?.phone}` : ""),
        };

      default:
        return { data: [], getOptionLabel: (opt) => opt };
    }
  }, [_watchSourceModel, customerRes, vendorRes, contractorRes]);

  // methods
  const handlePayload = (payload) => {
    const _payload = {
      ...payload,
      sourceModelRef: payload?.sourceModelRef?._id,
      sourceModel:
        payload?.sourceModel === PAYMENT_ACCOUNT_SOURCE_MODEL.VENDOR_PROFILE
          ? "VendorProfile"
          : payload?.sourceModel ===
            PAYMENT_ACCOUNT_SOURCE_MODEL.CUSTOMER_PROFILE
          ? "Customer"
          : payload?.sourceModel ===
            PAYMENT_ACCOUNT_SOURCE_MODEL.CONTRACTOR_PROFILE
          ? "Contractor"
          : "",
      account: payload?.account?._id,
      entity: payload?.entity?._id,
    };

    onSubmit(_payload);
  };

  React.useEffect(() => {
    if (!_watchAccount) return;
    fetchEntities({ account: _watchAccount?._id });
  }, [fetchEntities, _watchAccount]);

  React.useEffect(() => {
    if (
      !_watchEntity?._id ||
      _watchSourceModel === !PAYMENT_ACCOUNT_SOURCE_MODEL.VENDOR_PROFILE
    )
      return;
    fetchVendors({ entity: _watchEntity?._id });
  }, [_watchEntity?._id, _watchSourceModel, fetchVendors]);

  React.useEffect(() => {
    if (
      !_watchEntity?._id ||
      _watchSourceModel === !PAYMENT_ACCOUNT_SOURCE_MODEL.CUSTOMER_PROFILE
    )
      return;
    fetchCustomers({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    _watchSourceModel,
    fetchCustomers,
  ]);
  React.useEffect(() => {
    if (
      !_watchEntity?._id ||
      _watchSourceModel === !PAYMENT_ACCOUNT_SOURCE_MODEL.CONTRACTOR_PROFILE
    )
      return;
    fetchContractors({
      account: _watchAccount?._id,
      entity: _watchEntity?._id,
    });
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    _watchSourceModel,
    fetchContractors,
  ]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchVendors({ entity: _watchEntity?._id });
  }, [_watchEntity?._id, fetchVendors]);

  React.useEffect(() => {
    if (!paymentAccountDetail) return;

    reset({
      ...paymentAccountDetail,
      sourceModel:
        paymentAccountDetail?.sourceModel === "VendorProfile"
          ? "Vendor Profile"
          : paymentAccountDetail?.sourceModel === "Customer"
          ? "Customer Profile"
          : "Contractor Profile",
    });
  }, [paymentAccountDetail, reset]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={3}>
          {(isAccountAdmin || isSystemAdmin) && (
            <>
              <Grid item xs={6}>
                <Controller
                  name="account"
                  control={control}
                  rules={{ required: "Please select an Account." }}
                  render={(props) => (
                    <BaseAutocomplete
                      {...formHookInputHelper(props)}
                      required
                      fullWidth
                      label="Account"
                      options={accounts ?? []}
                      getOptionLabel={(opt) => opt && opt?.name}
                      isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                      onChange={(_, account) => {
                        setValue("account", account);
                        setValue("entity", "");
                      }}
                      loading={loadingAccounts || fetchingAccounts}
                      loadingText="Loading Accounts..."
                      disabled={!isSystemAdmin}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="entity"
                  control={control}
                  rules={{ required: "Please select an Entity." }}
                  render={(props) => (
                    <BaseAutocomplete
                      {...formHookInputHelper(props)}
                      required
                      fullWidth
                      label="Entity"
                      options={entities ?? []}
                      getOptionLabel={(opt) => opt && opt?.name}
                      isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                      onChange={(_, entity) => {
                        setValue("entity", entity);
                      }}
                      loading={loadingEntities || fetchingEntities}
                      loadingText="Loading Entities..."
                      disabled={isEntityAdmin}
                    />
                  )}
                />
              </Grid>
            </>
          )}
          <Grid item xs={6}>
            <Controller
              name="accountName"
              control={control}
              rules={{ required: "Please provide a Account Name " }}
              render={(props) => (
                <BaseTextField
                  fullWidth
                  required
                  label="Account Name"
                  {...formHookInputHelper(props)}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="accountNumber"
              control={control}
              rules={{ required: "Please provide a Account Number " }}
              render={(props) => (
                <BaseTextField
                  fullWidth
                  required
                  label="Account Number"
                  {...formHookInputHelper(props)}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="sourceModel"
              control={control}
              rules={{ required: "Please provide sourceModel" }}
              render={(props) => (
                <BaseSelect
                  {...formHookInputHelper(props)}
                  fullWidth
                  required
                  type="text"
                  label="Source"
                  options={Object.values(PAYMENT_ACCOUNT_SOURCE_MODEL)}
                  onChange={(e) => {
                    // Trigger the default onChange for the field
                    props.field.onChange(e.target.value);
                    // Clear the sourceModelRef when sourceModel changes
                    setValue("sourceModelRef", null);
                  }}
                />
              )}
            />
          </Grid>
          {_watchSourceModel && (
            <Grid item xs={6}>
              <Controller
                name="sourceModelRef"
                control={control}
                rules={{ required: `Please select a ${sourceRefLabel}` }}
                render={(props) => (
                  <BaseAutocomplete
                    {...formHookInputHelper(props)}
                    fullWidth
                    required
                    label={sourceRefLabel}
                    loading={
                      loadingVendors ||
                      fetchingVendors ||
                      loadingCustomer ||
                      fetchingCustomer ||
                      loadingContractor ||
                      fetchingContractor
                    }
                    options={originSourceRefInput?.data || []}
                    getOptionLabel={originSourceRefInput?.getOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                      option._id === value?._id
                    }
                    onChange={(_, value) => {
                      setValue("sourceModelRef", value);
                    }}
                    value={props.field.value} // Ensure value is updated
                    disabled={!_watchEntity || loadingVendors}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={12}>
            <FormHelperText>Description*</FormHelperText>

            <Controller
              name="description"
              control={control}
              rules={{ required: "Please provide a description." }}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={
                    paymentAccountDetail?.description
                      ? paymentAccountDetail.description
                      : "<p>Enter Payment Account Detail Description.</p>"
                  }
                  onChange={onChange}
                  height={200}
                />
              )}
            />
            {errors.description && (
              <FormHelperText error>
                {errors.description.message}
              </FormHelperText>
            )}
          </Grid>
          <Grid item md={6}>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Is Active?"
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button disabled={isBusy} onClick={() => navigate(-1)}>
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={isBusy}
          onClick={handleSubmit(handlePayload)}
        >
          {paymentAccountDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default PaymentAccountForm;
